import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import ApprovalCard from './ApprovalCard';
import { APPROVAL_TYPES } from './approvalTypes';

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'returned', label: 'Sent back' }
];

const EMPTY_MESSAGE = {
  pending: 'Nothing waiting for approval right now.',
  approved: 'No approved items yet.',
  rejected: 'No rejected items yet.',
  returned: 'Nothing sent back to an agent.'
};

function ApprovalsPanel({ condoId, onCountsChange }) {
  const { getToken } = useAuth();

  const [statusFilter, setStatusFilter] = useState('pending');
  const [typeFilter, setTypeFilter] = useState('all');
  const [approvals, setApprovals] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const fetchApprovals = useCallback(async () => {
    try {
      setLoading(true);
      const params = { status: statusFilter, condo: condoId };
      if (typeFilter !== 'all') params.type = typeFilter;
      const response = await axios.get(`${getApiBaseUrl()}/api/admin/approvals`, {
        params,
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setApprovals(response.data.approvals || []);
      const nextCounts = response.data.counts || { pending: 0, approved: 0, rejected: 0 };
      setCounts(nextCounts);
      onCountsChange?.(nextCounts);
    } catch (err) {
      console.error('Failed to fetch approvals:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condoId, statusFilter, typeFilter]);

  useEffect(() => {
    fetchApprovals();
  }, [fetchApprovals]);

  const handleApprove = async (id) => {
    setBusyId(id);
    setBusyAction('approve');
    try {
      const response = await axios.post(
        `${getApiBaseUrl()}/api/admin/approvals/${id}/approve`,
        {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSuccessMessage(response.data?.message?.startsWith('Sent to') ? `${response.data.message}.` : 'Approved.');
      fetchApprovals();
    } catch (err) {
      console.error('Failed to approve:', err);
      alert(err.response?.data?.error || 'Failed to approve');
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  const handleReject = async (id, title, { reasonRequired = false } = {}) => {
    let note;
    if (reasonRequired) {
      note = window.prompt(`Why are you rejecting "${title}"? The reason goes back to the agent; council won't see this item.`);
      if (!note || !note.trim()) return;
    } else if (!window.confirm(`Reject and discard "${title}"?`)) {
      return;
    }
    setBusyId(id);
    setBusyAction('reject');
    try {
      await axios.post(
        `${getApiBaseUrl()}/api/admin/approvals/${id}/reject`,
        note ? { note } : {},
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSuccessMessage('Rejected.');
      fetchApprovals();
    } catch (err) {
      console.error('Failed to reject:', err);
      alert(err.response?.data?.error || 'Failed to reject');
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  const handleReturn = async (id) => {
    const feedback = window.prompt('What should the agent change? It will propose a new version.');
    if (!feedback || !feedback.trim()) return;
    setBusyId(id);
    setBusyAction('return');
    try {
      await axios.post(
        `${getApiBaseUrl()}/api/admin/approvals/${id}/return`,
        { feedback },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSuccessMessage('Sent back to the agent.');
      fetchApprovals();
    } catch (err) {
      console.error('Failed to send back:', err);
      alert(err.response?.data?.error || 'Failed to send back');
    } finally {
      setBusyId(null);
      setBusyAction(null);
    }
  };

  const handleSave = async (id, payload) => {
    try {
      await axios.put(
        `${getApiBaseUrl()}/api/admin/approvals/${id}`,
        { payload },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      setSuccessMessage('Updated.');
      fetchApprovals();
    } catch (err) {
      console.error('Failed to update approval:', err);
      alert(err.response?.data?.error || 'Failed to update');
      throw err;
    }
  };

  return (
    <div>
      <p className="text-muted-foreground mb-6">Review AI-proposed content and actions before they go live</p>

      {successMessage && (
        <div className="bg-primary/10 border border-primary text-primary px-4 py-3 rounded mb-6">
          {successMessage}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="flex gap-2">
          {STATUS_FILTERS.filter((f) => f.value !== 'returned' || counts.returned > 0 || statusFilter === 'returned').map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-secondary-foreground hover:bg-secondary/80'
              }`}
            >
              {f.label} ({counts[f.value] ?? 0})
            </button>
          ))}
        </div>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="ml-auto px-3 py-2 bg-card border border-border rounded-lg text-foreground text-sm"
        >
          <option value="all">All types</option>
          {Object.entries(APPROVAL_TYPES).map(([type, entry]) => (
            <option key={type} value={type}>{entry.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : approvals.length === 0 ? (
        <div className="bg-card rounded-lg shadow-md p-8 border border-border text-center text-muted-foreground">
          {EMPTY_MESSAGE[statusFilter] || 'Nothing here.'}
        </div>
      ) : (
        <div className="space-y-4">
          {approvals.map((approval) => (
            <ApprovalCard
              key={approval._id}
              approval={approval}
              onApprove={handleApprove}
              onReject={handleReject}
              onReturn={handleReturn}
              onSave={handleSave}
              busy={busyId === approval._id ? busyAction : null}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default ApprovalsPanel;
